import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import {
  fadeSlide,
  InOutAnimation,
} from 'src/app/shared/animations/animations';
import {
  AccountSetupHelperService,
  WebsiteIdeaStepKey,
  WebsiteIdeaStepStatusEnum,
} from '../../account-setup-helper.service';

@Component({
  selector: 'app-hub-idea-step',
  templateUrl: './hub-idea-step.component.html',
  styleUrls: ['./hub-idea-step.component.css'],
  animations: [InOutAnimation, fadeSlide],
})
export class HubIdeaStepComponent implements OnInit, OnChanges {
  /** 👇 External step input */
  @Input() activeStep?: number | WebsiteIdeaStepKey;

  manualNavigationLocked = true;
  readonly stepStatusEnum = WebsiteIdeaStepStatusEnum;

  currentStep = 0;
  isGenerating = false;

  triggerCounter: Record<WebsiteIdeaStepKey, number> = {
    idea: 0,
    audience: 0,
    goals: 0,
  };

  prompts: Record<WebsiteIdeaStepKey, string> = {
    idea: '',
    audience: '',
    goals: '',
  };

  constructor(public accountSetupHelperService: AccountSetupHelperService) {}

  ngOnInit(): void {
    const aiState = this.accountSetupHelperService.wizardData.hubInput.aiState;

    if (!aiState.aiData) {
      aiState.aiData = { idea: '', audience: '', goals: '' };
    }

    this.generateSubPrompts();

    if (aiState.aiData.idea) {
      this.setStepStatus('idea', WebsiteIdeaStepStatusEnum.done);
    }
    if (aiState.aiData.audience) {
      this.setStepStatus('audience', WebsiteIdeaStepStatusEnum.done);
    }
    if (aiState.aiData.goals) {
      this.setStepStatus('goals', WebsiteIdeaStepStatusEnum.done);
    }

    if (aiState.aiData.idea && aiState.aiData.audience && aiState.aiData.goals) {
      this.manualNavigationLocked = false;
      this.accountSetupHelperService.setAiLoaderState('idle');
    } else if (!aiState.aiData.idea) {
      this.accountSetupHelperService.setAiLoaderMessage('Thinking...');
      this.startGenerationFlow('idea');
    }
  }

  /** 👇 Detect external step changes */
  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['activeStep'] &&
      this.activeStep !== undefined &&
      this.activeStep !== null
    ) {
      this.handleExternalStepChange(this.activeStep);
    }
  }

  private async handleExternalStepChange(step: number | WebsiteIdeaStepKey) {
    let stepIndex = -1;

    if (typeof step === 'string') {
      stepIndex = this.accountSetupHelperService.WebsiteIdeaSteps.findIndex(
        (s) => s.key === step,
      );
    } else {
      stepIndex = step;
    }

    if (
      stepIndex >= 0 &&
      stepIndex < this.accountSetupHelperService.WebsiteIdeaSteps.length
    ) {
      await this.goToStep(stepIndex);
    }
  }

  setStepStatus(
    step: WebsiteIdeaStepKey,
    status: WebsiteIdeaStepStatusEnum,
  ): void {
    const stepIndex = this.accountSetupHelperService.WebsiteIdeaSteps.findIndex(
      (s) => s.key === step,
    );
    if (stepIndex !== -1) {
      this.accountSetupHelperService.WebsiteIdeaSteps[stepIndex].status =
        status;
    }
  }

  async onStepDone(): Promise<void> {
    // const isThereNextStep = this.isThereNextStep;
    this.setStepStatus(
      this.accountSetupHelperService.WebsiteIdeaSteps[this.currentStep].key,
      WebsiteIdeaStepStatusEnum.done,
    );

    // if (!isThereNextStep) {
    this.accountSetupHelperService.setAiLoaderMessage('Done!', 4000);
    this.accountSetupHelperService.setAiLoaderState('idle');
    this.manualNavigationLocked = false;
    this.accountSetupHelperService.scheduleSaveProgress();
    // } else {
    //   await new Promise(resolve => setTimeout(resolve, 3000));
    //   await this.nextStep();
    // }
  }

  private startGenerationFlow(step: WebsiteIdeaStepKey): void {
    this.accountSetupHelperService.setAiLoaderState('thinking');
    const messages: Record<WebsiteIdeaStepKey, string> = {
      idea: 'Expanding on Hub Idea...',
      audience: 'Determining Audience...',
      goals: 'Identifying Goals...',
    };

    this.accountSetupHelperService.setAiLoaderMessage(messages[step]);
    this.setStepStatus(step, WebsiteIdeaStepStatusEnum.inProgress);
    this.triggerGenerate(step);
    this.accountSetupHelperService.setAiLoaderState('writing');
  }

  navigateToStep(step: number, force = false): void {
    if (this.isGenerating) return;
    if (this.manualNavigationLocked && !force) return;

    // Can only navigate to completed steps or earlier steps
    const targetStep = this.steps[step];
    if (!targetStep) return;

    // Allow navigation to completed steps or going backward
    if (
      step < this.currentStep ||
      targetStep.status === this.stepStatusEnum.done
    ) {
      this.currentStep = step;
    }
  }

  canNavigateToStep(stepIndex: number): boolean {
    if (this.isGenerating) return false;
    if (this.manualNavigationLocked) return false;

    const targetStep = this.steps[stepIndex];
    if (!targetStep) return false;

    // Can navigate to completed steps or earlier steps
    return (
      stepIndex < this.currentStep ||
      stepIndex === this.currentStep ||
      targetStep.status === this.stepStatusEnum.done
    );
  }

  get steps() {
    return this.accountSetupHelperService.WebsiteIdeaSteps;
  }

  get isThereNextStep() {
    return !(
      this.currentStep >=
      this.accountSetupHelperService.WebsiteIdeaSteps.length - 1
    );
  }

  async goToStep(index: number): Promise<void> {
    // Guard: if index is out of range, do nothing
    if (
      index < 0 ||
      index >= this.accountSetupHelperService.WebsiteIdeaSteps.length
    )
      return;

    const targetStepKey =
      this.accountSetupHelperService.WebsiteIdeaSteps[index].key;
    const aiData =
      this.accountSetupHelperService.wizardData.hubInput.aiState.aiData;

    // Optional: Prevent duplicate processing
    if (this.isGenerating) return;

    this.isGenerating = true;
    this.currentStep = index;
    this.isGenerating = false;

    // Start generation flow if this step hasn’t been generated yet
    if (!aiData[targetStepKey]) {
      await this.startGenerationFlow(targetStepKey);
    }
  }

  async nextStep(): Promise<void> {
    await this.goToStep(this.currentStep + 1);
  }

  prevStep(): void {
    if (this.currentStep > 0 && !this.isGenerating) {
      this.currentStep--;
    }
  }

  private triggerGenerate(target: WebsiteIdeaStepKey): void {
    this.triggerCounter[target]++;
  }

  private generateSubPrompts(
    hubIdea = this.accountSetupHelperService.wizardData.hubInput.aiState.aiSeed,
  ): void {
    this.prompts = {
      idea: `Expand on this hub idea: "${hubIdea}". Provide a very short description of the academy or website in 1-2 sentences. 
      Rules:
      - Don't Specify any name or title or any other specific details.
      - Don't use any kind of formatting.`,
      audience: `Based on this hub idea: "${hubIdea}", describe the ideal target learners or users (age, background, skill level, motivations, challenges). Provide a very short description in 1-2 sentences. 
      Rules:
      - Don't Specify any name or title or any other specific details.
      - Don't use any kind of formatting.`,
      goals: `Given the hub idea: "${hubIdea}", suggest the main objectives and value it should deliver to learners. Provide 80–120 words, written in 2–3 short paragraphs. 
      Rules:
      - Don't Specify any name or title or any other specific details.
      - Don't use any kind of formatting.`,
    };
  }
}
